/**
 * SEO helper — sets <title>, meta description, and OpenGraph/Twitter tags on route mount.
 * Lightweight alternative to react-helmet-async (avoid adding a dep).
 */
import { useEffect } from "react";

const SITE_NAME = "Arre Sonora";
const SITE_URL = "https://www.arresonora.mx";
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function clearJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function useSEO({ title, description, image, url, type = "website", jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Boutique de Carnes 100% Sonorense`;
    const desc = description || "Carne premium 100% sonorense, la mejor calidad de México. Envío mismo día en CDMX y EDOMEX.";
    const img = image || DEFAULT_IMAGE;
    const canonical = url || `${SITE_URL}${window.location.pathname}`;

    document.title = fullTitle;
    setMeta("description", desc);
    setLink("canonical", canonical);

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:image", img, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", SITE_NAME, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", img);

    // JSON-LD structured data
    if (jsonLd) {
      setJsonLd("ld-page", jsonLd);
    } else {
      clearJsonLd("ld-page");
    }
  }, [title, description, image, url, type, jsonLd]);
}

export function buildProductLd({ name, description, image, slug, price, sku }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image,
    sku,
    brand: { "@type": "Brand", name: SITE_NAME },
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/producto/${slug}`,
      priceCurrency: "MXN",
      price: String(price),
      availability: "https://schema.org/InStock",
    },
  };
}

export function buildArticleLd({ title, excerpt, image, slug, created_at, updated_at }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: excerpt,
    image,
    datePublished: created_at,
    dateModified: updated_at || created_at,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
      "@type": "Organization", name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
  };
}

export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: "Boutique de carnes premium 100% sonorense.",
  sameAs: [],
};
